import { Workspace, WorkspacesListingItem } from '@futrli-sdk/workspaces';
import {
  getGivenNameSelector,
  getBusinessPhoneNumberSelector,
  getBusinessTypeSelector,
  ELOQUA_LS_COUNTRY_KEY,
  ELOQUA_LS_OPTED_INTO_MARKETING_KEY,
  sendEloquaRequest
} from '@futrli/auth';
import { injectIntl, WrappedComponentProps } from '@futrli/localisation';
import {
  AnyFn,
  PREDICT_PRODUCT_ID,
  createWorkspace,
  getIntegrationsOnboardingUrl,
  getRootUrl,
  getWorkspaces,
  workspacesSelector
} from '@futrli/shared-services';
import { get } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { useNavigate, NavigateFunction } from 'react-router-dom';
import { StoreState } from '../../core/redux-utils/types';
import { OnboardingIntro } from '../components/onboarding-intro';
import { OnboardingLoader } from '../components/onboarding-loader';

interface RouterProps {
  navigate: NavigateFunction;
}

interface State {
  readonly workspaceId: string | null;
  readonly step: Steps;
  readonly error: string | undefined;
}

interface StateToProps {
  readonly name: string | null;
  readonly workspaces: Workspace[] | WorkspacesListingItem[] | null;
  readonly businessType: string;
  readonly businessPhoneNumber: string;
}

interface DispatchToProps {
  readonly createWorkspace: typeof createWorkspace;
  readonly getWorkspaces: typeof getWorkspaces;
}

export type Props = WrappedComponentProps & RouterProps & StateToProps & DispatchToProps;

const STEP_DELAY = 1000;

export enum Steps {
  INIT,
  CREATE_WORKSPACE,
  VERIFY_SUCCESS,
  DONE,
  ERROR
}

export class OnboardingContainerBase extends React.PureComponent<Props, State> {
  public state: State = {
    step: Steps.INIT,
    workspaceId: null,
    error: undefined
  };

  public componentDidMount(): void {
    const { workspaces } = this.props;

    if (!workspaces) {
      this.props.getWorkspaces();
    }
    this.startOnboarding();
  }

  public componentDidUpdate() {
    this.startOnboarding();
  }

  private startOnboarding() {
    const { name, workspaces } = this.props;
    const { step } = this.state;

    // Bail out and send the user to root if they have workspaces.
    if (workspaces && workspaces.length > 0) {
      return this.props.navigate(getRootUrl());
    }

    // If we have everything, begin the automated steps
    if (workspaces !== null && step === Steps.INIT && name) {
      return this.goToStep(Steps.CREATE_WORKSPACE);
    }
  }

  public render(): JSX.Element {
    const { step, workspaceId } = this.state;

    if (step === Steps.DONE) {
      return <OnboardingIntro workspaceId={workspaceId || ''} />;
    }

    return <OnboardingLoader />;
  }

  /**
   * Changes the step state to the given step, and decides which step
   * method to call next.
   *
   * @param {Steps} step
   */
  private async goToStepCallback(step: Steps): Promise<void> {
    await this.setState({ step });
    switch (step) {
      default:
      case Steps.INIT:
      case Steps.CREATE_WORKSPACE:
        this.createWorkspace();
        break;
      case Steps.VERIFY_SUCCESS:
        this.verifySuccess();
        break;
      case Steps.DONE:
        break;
    }
  }

  /**
   * Small wrapper to abstract the setTimeout from the rest of
   * the logic.
   *
   * @param {Steps} step
   */
  private goToStep(step: Steps): void {
    this.withTimeout(() => this.goToStepCallback(step));
  }

  /**
   * Small helper to apply setTimeout.
   */
  private readonly withTimeout = (callback: AnyFn) => window.setTimeout(callback, STEP_DELAY);

  /**
   * Generates the bare minimum we need for a payload to create a workspace.
   *
   * @returns {Partial<Workspace>}
   */
  private generateWorkspace(): Partial<Workspace> {
    const {
      name,
      intl: { formatMessage }
    } = this.props;

    return {
      name: formatMessage(
        {
          id: 'landing.default_workspace_name',
          defaultMessage: `{name}'s workspace`
        },
        { name }
      )
    };
  }

  /**
   * Step to create a workspace.
   *
   * @returns {Promise<void>}
   */
  private async createWorkspace(): Promise<void> {
    try {
      const response = await this.props.createWorkspace(this.generateWorkspace());
      const workspaceId = get(response, 'data.result');

      if (!workspaceId) {
        throw new Error('Failed to create workspace');
      }

      await this.setState({ workspaceId });

      return this.goToStep(Steps.VERIFY_SUCCESS);
    } catch (error) {
      return this.handleError(error as never);
    }
  }

  /**
   * Just verify that we have the correct data.
   */
  private verifySuccess(): void {
    const { workspaceId } = this.state;
    const { businessPhoneNumber, businessType } = this.props;

    try {
      if (!workspaceId) {
        throw new Error('Failed to create workspace');
      }

      const country = localStorage.getItem(ELOQUA_LS_COUNTRY_KEY);
      const opted_into_marketing = localStorage.getItem(ELOQUA_LS_OPTED_INTO_MARKETING_KEY);
      sendEloquaRequest(country, businessPhoneNumber, opted_into_marketing === 'true', businessType);
      localStorage.removeItem(ELOQUA_LS_COUNTRY_KEY);
      localStorage.removeItem(ELOQUA_LS_OPTED_INTO_MARKETING_KEY);

      this.goToStep(Steps.DONE);
    } catch (error) {
      this.handleError(error as never);
    }
  }

  /**
   * Step to redirect the user.
   */
  private done(): void {
    this.withTimeout(() => this.props.navigate(this.getExitUrl()));
  }

  /**
   * Handles the given error by adding it to the state and short-circuiting
   * the current step.
   *
   * @param {Error} error
   * @returns {Promise<void>}
   */
  private async handleError(error: Error): Promise<void> {
    return await this.setState({ step: Steps.ERROR, error: error.message });
  }

  /**
   * Gets the correct url to exit this flow.
   *
   * @returns {string}
   */
  private getExitUrl(): string {
    const { workspaceId } = this.state;
    return getIntegrationsOnboardingUrl(workspaceId || '', '', PREDICT_PRODUCT_ID);
  }
}

const mapStateToProps = (store: StoreState): StateToProps => {
  const { authReducers, normalised } = store;

  return {
    name: getGivenNameSelector(authReducers),
    businessType: getBusinessTypeSelector(authReducers),
    businessPhoneNumber: getBusinessPhoneNumberSelector(authReducers),
    workspaces: workspacesSelector(normalised)
  };
};

const mapDispatchToProps: DispatchToProps = {
  createWorkspace,
  getWorkspaces
};

export const OnboardingContainerWS = connect(mapStateToProps, mapDispatchToProps)(injectIntl(OnboardingContainerBase));

export const OnboardingContainer: React.FC = () => {
  const navigate = useNavigate();
  return <OnboardingContainerWS navigate={navigate} />;
};
